import React from 'react';
import clsx from 'clsx';

import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import { ArrowDownIcon } from '@smart-tv/components/icons/arrow-down';
import DragScroll from '@smart-tv/components/drag-scroll/drag-scroll';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

import styles from './horizontal-slider.module.scss';

interface Props {
  listRef: React.RefObject<HTMLDivElement>;
  navigationStateKey: string;
  prevLabel?: string;
  nextLabel?: string;
  className?: string;
  listClassName?: string;
  contentClassName?: string;
  navClassName?: string;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  children: React.ReactNode;
}

// Horizontally scrolling list (DragScroll) with prev/next overlay navigation arrows.
export const HorizontalSlider: React.FC<Props> = ({
  listRef,
  navigationStateKey,
  prevLabel = 'Forrige',
  nextLabel = 'Næste',
  className,
  listClassName,
  navClassName,
  contentClassName,
  onMouseEnter,
  children
}) => {
  const [prevDisabled, setPrevDisabled] = React.useState(true);
  const [nextDisabled, setNextDisabled] = React.useState(false);

  const updateNavigationState = React.useCallback(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    const hasOverflow = listElement.scrollWidth > listElement.clientWidth + 1;

    if (!hasOverflow) {
      setPrevDisabled(true);
      setNextDisabled(true);
      return;
    }

    setPrevDisabled(listElement.scrollLeft <= 0);
    setNextDisabled(
      listElement.scrollLeft + listElement.clientWidth >= listElement.scrollWidth - 1
    );
  }, [listRef]);

  const handleScroll = React.useCallback(
    (forward: boolean) => {
      const listElement = listRef.current;
      if (!listElement) return;

      const amount = (listElement.clientWidth * 4) / 5;
      listElement.scrollBy({ left: forward ? amount : -amount, behavior: 'smooth' });
    },
    [listRef]
  );

  const handleScrollPrev = React.useCallback(() => handleScroll(false), [handleScroll]);
  const handleScrollNext = React.useCallback(() => handleScroll(true), [handleScroll]);

  React.useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    updateNavigationState();
    const rafId = window.requestAnimationFrame(updateNavigationState);
    listElement.addEventListener('scroll', updateNavigationState);
    window.addEventListener('resize', updateNavigationState);

    return () => {
      window.cancelAnimationFrame(rafId);
      listElement.removeEventListener('scroll', updateNavigationState);
      window.removeEventListener('resize', updateNavigationState);
    };
  }, [listRef, navigationStateKey, updateNavigationState]);

  return (
    <div className={clsx(styles['horizontal-slider'], className)}>
      <DragScroll
        innerRef={listRef}
        direction="horizontal"
        disableDrag={!IS_BROWSER}
        hideScrollbar={true}
        className={clsx(styles['horizontal-slider__list'], listClassName)}
        onMouseEnter={onMouseEnter}
      >
        <div className={clsx(styles['horizontal-slider__content'], contentClassName)}>
          {children}
        </div>
      </DragScroll>
      {IS_BROWSER && (
        <div
          className={clsx(styles['horizontal-slider__nav'], navClassName, {
            [styles['horizontal-slider__nav--hidden']]: prevDisabled && nextDisabled
          })}
        >
          <IconButton
            className={clsx(
              styles['horizontal-slider__arrow'],
              styles['horizontal-slider__arrow--prev'],
              'horizontal-slider__arrow--prev'
            )}
            size="lg"
            background={true}
            onClick={handleScrollPrev}
            disabled={prevDisabled}
            aria-label={prevLabel}
            icon={<ArrowDownIcon />}
          />
          <IconButton
            className={clsx(
              styles['horizontal-slider__arrow'],
              styles['horizontal-slider__arrow--next'],
              'horizontal-slider__arrow--next'
            )}
            size="lg"
            background={true}
            onClick={handleScrollNext}
            disabled={nextDisabled}
            aria-label={nextLabel}
            icon={<ArrowDownIcon />}
          />
        </div>
      )}
    </div>
  );
};

export default HorizontalSlider;

